import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface ShoppingItem {
  id: string;
  item_name: string;
  quantity: number;
  unit: string;
  category: string;
  is_purchased: boolean;
  notes: string;
}

interface ShoppingListData {
  id: string;
  name: string;
  week_start_date: string;
  is_auto_generated: boolean;
  total_items: number;
  purchased_items: number;
}

const ShoppingList: React.FC = () => {
  const [lists, setLists] = useState<ShoppingListData[]>([]);
  const [activeList, setActiveList] = useState<ShoppingListData | null>(null);
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewListModal, setShowNewListModal] = useState(false);
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editListName, setEditListName] = useState('');
  const [newItem, setNewItem] = useState({ itemName: '', quantity: 1, unit: '', category: '', notes: '' });

  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getShoppingLists();
      if (response.success) {
        setLists(response.lists || []);
        if (response.lists?.length > 0 && !activeList) {
          setActiveList(response.lists[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch shopping lists:', error);
    } finally {
      setLoading(false);
    }
  }, [activeList]);

  const fetchListItems = useCallback(async () => {
    if (!activeList) return;
    try {
      const response = await apiService.getShoppingList(activeList.id);
      if (response.success) {
        setItems(response.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    }
  }, [activeList]);

  useEffect(() => { fetchLists(); }, []);
  useEffect(() => { fetchListItems(); }, [activeList, fetchListItems]);

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      await apiService.createShoppingList({ name: newListName });
      setNewListName('');
      setShowNewListModal(false);
      fetchLists();
    } catch (error) {
      console.error('Failed to create list:', error);
    }
  };

  const handleGenerateFromMeals = async () => {
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    try {
      const response = await apiService.generateShoppingFromMeals(weekStart.toISOString().split('T')[0]);
      if (response.success) {
        alert(`Generated ${response.items?.length || 0} items from your meal plans!`);
        fetchLists();
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to generate shopping list');
    }
  };

  const handleEditList = async () => {
    if (!activeList || !editListName.trim()) return;
    try {
      await apiService.updateShoppingList(activeList.id, { name: editListName.trim() });
      setShowEditListModal(false);
      fetchLists();
    } catch (error) {
      console.error('Failed to update list:', error);
      alert('Failed to update list');
    }
  };

  const handleDeleteList = async () => {
    if (!activeList) return;
    if (!window.confirm(`Are you sure you want to delete "${activeList.name}"? All items will be removed.`)) return;
    try {
      await apiService.deleteShoppingList(activeList.id);
      setActiveList(null);
      setItems([]);
      fetchLists();
    } catch (error) {
      console.error('Failed to delete list:', error);
      alert('Failed to delete list');
    }
  };

  const openEditListModal = () => {
    if (activeList) {
      setEditListName(activeList.name);
      setShowEditListModal(true);
    }
  };

  const handleAddItem = async () => {
    if (!activeList || !newItem.itemName.trim()) return;
    try {
      await apiService.addShoppingItem(activeList.id, newItem);
      setNewItem({ itemName: '', quantity: 1, unit: '', category: '', notes: '' });
      setShowAddItemModal(false);
      fetchListItems();
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const handleToggleItem = async (itemId: string) => {
    try {
      await apiService.toggleShoppingItem(itemId);
      fetchListItems();
    } catch (error) {
      console.error('Failed to toggle item:', error);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      await apiService.deleteShoppingItem(itemId);
      fetchListItems();
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  const categories = ['produce', 'dairy', 'meat', 'bakery', 'pantry', 'frozen', 'beverages', 'other'];

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {} as Record<string, ShoppingItem[]>);

  return (
    <div style={{ padding: '24px' }} data-testid="shopping-list">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Shopping Lists</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Manage your grocery lists</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleGenerateFromMeals}
            style={{ padding: '10px 16px', backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
          >
            Generate from Meals
          </button>
          <button
            onClick={() => setShowNewListModal(true)}
            data-testid="create-list-btn"
            style={{ padding: '10px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
          >
            + New List
          </button>
        </div>
      </div>

      {/* List Tabs */}
      {lists.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '8px' }}>
          {lists.map(list => (
            <button
              key={list.id}
              onClick={() => setActiveList(list)}
              style={{
                padding: '10px 16px',
                backgroundColor: activeList?.id === list.id ? '#3b82f6' : 'white',
                color: activeList?.id === list.id ? 'white' : '#374151',
                border: activeList?.id === list.id ? 'none' : '1px solid #d1d5db',
                borderRadius: '8px',
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontSize: '0.9rem'
              }}
            >
              {list.name} ({list.purchased_items || 0}/{list.total_items || 0})
            </button>
          ))}
        </div>
      )}

      {/* Active List Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : !activeList ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>No shopping lists yet</p>
          <button
            onClick={() => setShowNewListModal(true)}
            style={{ padding: '12px 24px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
          >
            Create Your First List
          </button>
        </div>
      ) : (
        <div>
          {/* List Actions Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#0369a1' }}>{activeList.name}</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={openEditListModal}
                style={{ padding: '6px 12px', backgroundColor: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' }}
              >
                Edit
              </button>
              <button
                onClick={handleDeleteList}
                style={{ padding: '6px 12px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' }}
              >
                Delete
              </button>
            </div>
          </div>

          {/* Add Item Button */}
          <button
            onClick={() => setShowAddItemModal(true)}
            data-testid="add-item-btn"
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#f9fafb',
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              color: '#6b7280',
              fontWeight: 500,
              cursor: 'pointer',
              marginBottom: '16px'
            }}
          >
            + Add Item
          </button>

          {/* Items by Category */}
          {items.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>No items in this list</p>
          ) : (
            Object.entries(groupedItems).map(([category, catItems]) => (
              <div key={category} style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: 600, color: '#6b7280', textTransform: 'capitalize' }}>
                  {category}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {catItems.map(item => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: item.is_purchased ? '#f0fdf4' : 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        gap: '12px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={item.is_purchased}
                        onChange={() => handleToggleItem(item.id)}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <span style={{
                        flex: 1,
                        textDecoration: item.is_purchased ? 'line-through' : 'none',
                        color: item.is_purchased ? '#9ca3af' : '#1f2937'
                      }}>
                        {item.quantity} {item.unit} {item.item_name}
                      </span>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* New List Modal */}
      {showNewListModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Create New List</h3>
            <input
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="List name"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '16px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowNewListModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreateList} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#3b82f6', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Add Item</h3>
            <input
              type="text"
              value={newItem.itemName}
              onChange={(e) => setNewItem({ ...newItem, itemName: e.target.value })}
              placeholder="Item name"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <input
                type="number"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                min="1"
                style={{ width: '80px', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
              />
              <input
                type="text"
                value={newItem.unit}
                onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                placeholder="Unit (kg, pcs, etc)"
                style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px' }}
              />
            </div>
            <select
              value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '16px' }}
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddItemModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddItem} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#3b82f6', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit List Modal */}
      {showEditListModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Edit List</h3>
            <input
              type="text"
              value={editListName}
              onChange={(e) => setEditListName(e.target.value)}
              placeholder="List name"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '16px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowEditListModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleEditList} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#3b82f6', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingList;
